'use client';

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, Clock, Info, ShieldAlert, HeartPulse, Building2, Users, Wrench, ShoppingCart, Fuel, Croissant, Pill, Search, Bell, Droplets, Zap, ChevronUp, CloudSun, Church, Bus, ArrowRight, Newspaper, Car, Truck, Utensils, Pizza } from 'lucide-react';
import WeatherWidget from '@/components/WeatherWidget';
import RealTimeClock from '@/components/RealTimeClock';
import InstallButton from '@/components/InstallButton';
import Footer from '@/components/Footer';
import pharmaciesData from '../../public/plantao_farmacias_catanduvas_whatsapp.json';

interface Pharmacy {
  inicio: string;
  farmacia: string;
  endereco: string;
  telefone: string;
  whatsapp: string;
}

function getActivePharmacy(pharmacies: Pharmacy[]): Pharmacy | null {
  const now = new Date();
  
  // Sort by date ascending
  const sorted = [...pharmacies].sort((a, b) => new Date(a.inicio).getTime() - new Date(b.inicio).getTime());
  
  // Find the last record where inicio <= now
  let active = null;
  for (const pharmacy of sorted) {
    const start = new Date(pharmacy.inicio);
    if (start <= now) {
      active = pharmacy;
    } else {
      break;
    }
  }
  
  return active || (sorted.length > 0 ? sorted[0] : null);
}

function getNextPharmacy(pharmacies: Pharmacy[], activePharmacy: Pharmacy | null): Pharmacy | null {
  if (!activePharmacy) return null;
  const sorted = [...pharmacies].sort((a, b) => new Date(a.inicio).getTime() - new Date(b.inicio).getTime());
  const activeIndex = sorted.findIndex(p => p.inicio === activePharmacy.inicio);
  if (activeIndex >= 0 && activeIndex < sorted.length - 1) {
    return sorted[activeIndex + 1];
  }
  return null;
}

const TELEFONES_UTEIS = [
  { icon: <ShieldAlert />, name: "Polícia Militar", number: "190", color: "bg-blue-600" },
  { icon: <ShieldAlert />, name: "Polícia Civil", number: "(45) 3234-1333", color: "bg-slate-800" },
  { icon: <ShieldAlert />, name: "PRF", number: "191", color: "bg-slate-700" },
  { icon: <HeartPulse />, name: "SAMU", number: "192", color: "bg-red-600" },
  { icon: <Building2 />, name: "PAM - Pronto Atendimento", number: "(45) 3234-8585", color: "bg-indigo-600" },
  { icon: <HeartPulse />, name: "Centro de Saúde", number: "(45) 3234-8580", color: "bg-emerald-600" },
  { icon: <Building2 />, name: "Prefeitura", number: "(45) 3234-8500", color: "bg-blue-600" },
  { icon: <Users />, name: "CRAS", number: "(45) 3234-8562", color: "bg-purple-600" },
  { icon: <Users />, name: "Conselho Tutelar", number: "(45) 98822-1724", color: "bg-blue-800" },
  { icon: <Church />, name: "Paróquia São Sebastião", number: "(45) 3234-1234", color: "bg-amber-700" },
  { icon: <Users />, name: "ACEUC", number: "(45) 92002-8697", color: "bg-blue-500", whatsapp: "https://wa.me/5545920028697" },
  { icon: <Users />, name: "ACICA", number: "(45) 99105-4953", color: "bg-green-600", whatsapp: "https://wa.me/5545991054953", nameColor: "text-yellow-400", numberColor: "text-white" },
  { icon: <Building2 />, name: "IDR-Paraná", number: "(45) 98822-6814", color: "bg-green-700", whatsapp: "https://wa.me/5545988226814" },
  { icon: <Zap />, name: "Copel", number: "0800 51 00 116", color: "bg-[#FF4500]" },
  { icon: <Droplets />, name: "Sanepar", number: "0800 200 0115", color: "bg-sky-500" },
];

const SERVICOS_LOCAIS = [
  { icon: <Pill />, name: "Farmácias", action: "tel:4532340000" },
  { icon: <ShoppingCart />, name: "Mercados e Supermercados", action: "tel:4532340000" },
  { icon: <Fuel />, name: "Postos de Combustível", action: "tel:4532340000" },
  { icon: <Croissant />, name: "Panificadoras", action: "tel:4532340000" },
  { icon: <Wrench />, name: "Oficinas Mecânicas", action: "tel:4532340000" },
];

const COOPERATIVAS = [
  { name: "Sicredi", number: "(51) 3358-4770", color: "bg-[#39FF14]", icon: <Building2 />, whatsapp: "https://wa.me/555133584770" },
  { name: "Cresol", number: "(45) 3252-0270", color: "bg-orange-600", icon: <Building2 />, whatsapp: "https://wa.me/554532520270" },
  { name: "Sicoob", number: "(45) 9862-0284", color: "bg-[#009245]", icon: <Building2 />, whatsapp: "https://wa.me/554598620284" },
];

const LINKS_SITE = [
  { name: "Horários de Ônibus", description: "Horários e linhas de ônibus de Catanduvas", href: "/horarios-onibus-catanduvas", icon: <Bus />, color: "from-amber-500 to-orange-600" },
  { name: "Meu CEP", description: "Busca de ruas e CEP", href: "/cep", icon: <MapPin />, color: "from-blue-600 to-indigo-700" },
  { name: "ADM Municipal", description: "Telefones da Prefeitura", href: "/adm-municipal", icon: <Building2 />, color: "from-blue-600 to-indigo-700" },
  { name: "Comércio", description: "Empresas por Categoria", href: "/comercio", icon: <ShoppingCart />, color: "from-emerald-600 to-teal-700" },
  { name: "Táxis", description: "Número dos Taxistas", href: "/taxis", icon: <Car />, color: "from-amber-500 to-orange-600" },
  { name: "Guinchos", description: "Serviço 24h", href: "/guinchos", icon: <Truck />, color: "from-slate-600 to-slate-700" },
  { name: "Marmitas", description: "Pedir Marmitas", href: "/marmitas", icon: <Utensils />, color: "from-red-600 to-rose-700" },
  { name: "Lanches e Pizzas", description: "Pedir Lanches e Pizzas", href: "/lanches-pizzas", icon: <Pizza />, color: "from-amber-600 to-yellow-700" },
];

export default function HomeClient() {
  const [searchTerm, setSearchTerm] = useState('');
  const [mounted, setMounted] = useState(false);

  const [now, setNow] = useState(new Date());

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMounted(true);
    const timer = setInterval(() => setNow(new Date()), 10000);
    return () => clearInterval(timer);
  }, []);

  const brazilTime = new Date(now.toLocaleString("en-US", { timeZone: "America/Sao_Paulo" }));
  const hours = brazilTime.getHours();
  const minutes = brazilTime.getMinutes();
  const totalMinutes = hours * 60 + minutes;

  const isGreen = totalMinutes >= 480 && totalMinutes <= 1260; // 08:00 to 21:00

  const pharmacies: Pharmacy[] = pharmaciesData;
  const activePharmacy = getActivePharmacy(pharmacies);
  const nextPharmacy = getNextPharmacy(pharmacies, activePharmacy);

  const formatDate = (dateString: string) => {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Sao_Paulo'
    }).format(new Date(dateString));
  };

  let validityDate = new Date();
  if (nextPharmacy) {
    validityDate = new Date(nextPharmacy.inicio);
    validityDate.setMinutes(validityDate.getMinutes() - 1);
  } else if (activePharmacy) {
    validityDate = new Date(activePharmacy.inicio);
    validityDate.setDate(validityDate.getDate() + 7);
    validityDate.setMinutes(validityDate.getMinutes() - 1);
  }

  const formatPhoneNumber = (phone: string) => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11) {
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
    } else if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const filteredTelefones = TELEFONES_UTEIS.filter(t => 
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    t.number.includes(searchTerm)
  );

  const filteredServicos = SERVICOS_LOCAIS.filter(s => 
    s.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredCooperativas = COOPERATIVAS.filter(c => 
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    c.number.includes(searchTerm)
  );

  const filteredLinks = LINKS_SITE.filter(l => 
    l.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
    l.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 selection:bg-blue-200 selection:text-blue-900 pb-24">
      {/* Header */}
      <header className="bg-blue-700 text-white shadow-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col md:flex-row justify-between items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">CatanduvasPR.com.br</h1>
            <p className="text-blue-100 text-sm md:text-base font-medium">Portal de serviços e plantões da cidade</p>
          </div>
          <div className="flex items-center gap-3">
            <InstallButton />
            <RealTimeClock />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-12">
        
        {/* Campo de Busca */}
        <section className="relative max-w-2xl mx-auto">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Search className="h-6 w-6 text-slate-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-12 pr-4 py-4 bg-white border border-slate-200 rounded-2xl text-lg shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none"
              placeholder="Buscar telefones ou serviços..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </section>

        {/* Banner Farmácia de Plantão */}
        {!searchTerm && (
          <section aria-labelledby="plantao-title">
            <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-blue-100 relative min-h-[300px]">
              {!mounted ? (
                <div className="p-6 md:p-10 flex flex-col md:flex-row items-center gap-8 animate-pulse">
                  <div className="flex-1 space-y-6 w-full">
                    <div className="h-6 bg-slate-200 rounded-full w-48"></div>
                    <div className="h-12 bg-slate-200 rounded-lg w-3/4"></div>
                    <div className="space-y-3">
                      <div className="h-6 bg-slate-200 rounded w-full"></div>
                      <div className="h-20 bg-slate-200 rounded-2xl w-full"></div>
                    </div>
                  </div>
                  <div className="w-32 h-32 md:w-48 md:h-48 rounded-full bg-slate-200 flex-shrink-0"></div>
                </div>
              ) : (
                <>
                  <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-blue-500 to-indigo-600"></div>
                  <div className="p-6 md:p-10 flex flex-col md:flex-row items-center gap-8">
                    <div className="flex-1 space-y-6">
                      <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-1.5 rounded-full font-semibold text-sm uppercase tracking-wider">
                        <span className="relative flex h-3 w-3">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                          <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500"></span>
                        </span>
                        Farmácia de Plantão Atual
                      </div>
                      
                      {activePharmacy ? (
                        <>
                          <h2 id="plantao-title" className="text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight">
                            {activePharmacy.farmacia}
                          </h2>
                          
                          <div className="space-y-3 text-lg text-slate-600">
                            <p className="flex items-center gap-3">
                              <MapPin className="w-6 h-6 text-blue-600 flex-shrink-0" />
                              <span>{activePharmacy.endereco}</span>
                            </p>
                            <div className="flex items-start gap-3 bg-slate-50 p-4 rounded-2xl border border-slate-100">
                              <div className="bg-blue-100 p-2 rounded-lg">
                                <Clock className="w-5 h-5 text-blue-600" />
                              </div>
                              <div className="flex flex-col">
                                <span className="text-xs uppercase tracking-wider font-bold text-slate-400 leading-none mb-1">Vigência do Plantão</span>
                                <span className="text-slate-700 font-medium">
                                  Até <strong className="text-blue-700">o próximo sábado às 06:00</strong>
                                  <span className="text-xs text-slate-400 ml-2">
                                    ({formatDate(validityDate.toISOString()).split(',')[0]})
                                  </span>
                                </span>
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-col sm:flex-row gap-4 pt-4">
                            <a 
                              href={activePharmacy.whatsapp}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex justify-center items-center gap-2 bg-[#25D366] hover:bg-[#20bd5a] text-white px-6 py-3.5 rounded-xl font-bold text-lg transition-all shadow-lg shadow-green-200 hover:shadow-green-300 active:scale-95"
                            >
                              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
                              Chamar no WhatsApp
                            </a>
                            <a 
                              href={`tel:${activePharmacy.telefone}`}
                              className="inline-flex justify-center items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-800 px-6 py-3.5 rounded-xl font-bold text-lg transition-all"
                            >
                              <Phone className="w-5 h-5" />
                              {formatPhoneNumber(activePharmacy.telefone)}
                            </a>
                          </div>
                        </>
                      ) : (
                        <div className="p-8 bg-amber-50 rounded-xl border border-amber-200 text-amber-800">
                          <p className="font-medium flex items-center gap-2">
                            <Info className="w-5 h-5" />
                            Informações de plantão indisponíveis no momento.
                          </p>
                        </div>
                      )}
                    </div>
                    
                    <div className="flex flex-shrink-0 items-center justify-center">
                      <div className={`flex flex-col items-center justify-center w-32 h-32 md:w-48 md:h-48 rounded-full border-4 md:border-8 shadow-2xl transition-all duration-1000 animate-pulse relative ${
                        isGreen 
                          ? 'bg-green-500 border-green-100 text-white' 
                          : 'bg-red-600 border-red-200 text-white'
                      }`}>
                        <div className={`absolute inset-0 rounded-full animate-ping opacity-25 ${isGreen ? 'bg-green-400' : 'bg-red-500'}`}></div>
                        <span className="text-2xl md:text-4xl font-black uppercase tracking-tighter text-center leading-none z-10">
                          {isGreen ? 'Aberto' : 'PLANTÃO'}
                        </span>
                        <span className="text-[8px] md:text-[10px] font-bold mt-1 md:mt-2 opacity-80 z-10">
                          {isGreen ? 'Horário Comercial' : 'Atendimento Noturno'}
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </section>
        )}

        {/* Botões de Acesso Rápido */}
        {!searchTerm && (
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
            <Link 
              to="/horarios-onibus-catanduvas"
              className="group relative flex items-center justify-between bg-gradient-to-r from-amber-500 to-orange-600 p-6 rounded-3xl shadow-xl shadow-orange-100 hover:shadow-orange-200 transition-all hover:-translate-y-1 active:scale-[0.98] overflow-hidden"
            >
              <div className="absolute top-0 right-0 -mt-4 -mr-4 w-32 h-32 bg-white/10 rounded-full blur-2xl group-hover:bg-white/20 transition-colors"></div>
              <div className="flex items-center gap-5 relative z-10">
                <div className="bg-white/20 p-4 rounded-2xl backdrop-blur-sm">
                  <Bus className="w-8 h-8 text-white" />
                </div>
                <div className="flex flex-col">
                  <span className="text-white font-black text-2xl md:text-3xl uppercase tracking-tighter italic leading-none">
                    Ônibus
                  </span>
                  <span className="text-orange-100 font-bold text-sm md:text-base mt-1">
                    Horários e linhas
                  </span>
                </div>
              </div>
              <div className="bg-white/20 p-3 rounded-full backdrop-blur-sm relative z-10 group-hover:translate-x-1 transition-transform">
                <ArrowRight className="w-6 h-6 text-white" />
              </div>
            </Link>

            <Link 
              to="/cep"
              className="group relative flex items-center justify-between bg-gradient-to-r from-blue-600 to-indigo-700 p-6 rounded-3xl shadow-xl shadow-blue-100 hover:shadow-blue-200 transition-all hover:-translate-y-1 active:scale-[0.98] overflow-hidden"
            >
              <div className="absolute top-0 right-0 -mt-4 -mr-4 w-32 h-32 bg-white/10 rounded-full blur-2xl group-hover:bg-white/20 transition-colors"></div>
              <div className="flex items-center gap-5 relative z-10">
                <div className="bg-white/20 p-4 rounded-2xl backdrop-blur-sm">
                  <MapPin className="w-8 h-8 text-white" />
                </div>
                <div className="flex flex-col">
                  <span className="text-white font-black text-2xl md:text-3xl uppercase tracking-tighter italic leading-none">
                    Meu CEP
                  </span>
                  <span className="text-blue-100 font-bold text-sm md:text-base mt-1">
                    Busca de ruas
                  </span>
                </div>
              </div>
              <div className="bg-white/20 p-3 rounded-full backdrop-blur-sm relative z-10 group-hover:translate-x-1 transition-transform">
                <ArrowRight className="w-6 h-6 text-white" />
              </div>
            </Link>
          </div>
        )}

        {/* Telefones de Emergência (Substituindo Próximos Plantões) */}
        {!searchTerm && (
          <section className="max-w-5xl mx-auto">
            <div className="bg-red-600 rounded-[2rem] p-6 md:p-10 shadow-2xl shadow-red-200 relative overflow-hidden">
              {/* Elementos decorativos de fundo */}
              <div className="absolute -top-24 -right-24 w-64 h-64 bg-red-500 rounded-full blur-3xl opacity-50"></div>
              <div className="absolute -bottom-24 -left-24 w-64 h-64 bg-red-700 rounded-full blur-3xl opacity-50"></div>
              
              <div className="relative z-10">
                <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
                  <div>
                    <h2 className="text-white font-black text-3xl md:text-5xl flex items-center gap-4 uppercase tracking-tighter italic">
                      <ShieldAlert className="w-10 h-10 md:w-14 md:h-14 text-white animate-bounce" />
                      Emergência
                    </h2>
                    <p className="text-red-100 font-bold text-lg md:text-xl mt-2">Clique no botão para entrar em contato agora mesmo</p>
                  </div>
                  <div className="bg-white/20 backdrop-blur-md px-6 py-3 rounded-2xl border border-white/30 flex items-center gap-3">
                    <span className="relative flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-white"></span>
                    </span>
                    <span className="text-white text-sm font-black uppercase tracking-widest">Plantão 24h</span>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Polícia Militar */}
                  <a href="tel:190" className="bg-white hover:bg-red-50 p-5 rounded-2xl flex items-center gap-4 transition-all hover:scale-[1.02] active:scale-95 shadow-lg group border-l-8 border-blue-700">
                    <div className="bg-blue-100 p-3 rounded-xl group-hover:rotate-12 transition-transform">
                      <ShieldAlert className="w-8 h-8 text-blue-700" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-red-600 font-black text-xl md:text-2xl uppercase tracking-tighter leading-none">Polícia Militar</span>
                      <span className="text-blue-700 font-bold text-lg">190</span>
                    </div>
                  </a>

                  {/* SAMU */}
                  <a href="tel:192" className="bg-white hover:bg-red-50 p-5 rounded-2xl flex items-center gap-4 transition-all hover:scale-[1.02] active:scale-95 shadow-lg group border-l-8 border-red-600">
                    <div className="bg-red-100 p-3 rounded-xl group-hover:scale-110 transition-transform">
                      <HeartPulse className="w-8 h-8 text-red-600" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-red-600 font-black text-xl md:text-2xl uppercase tracking-tighter leading-none">SAMU</span>
                      <span className="text-red-600 font-bold text-lg">192</span>
                    </div>
                  </a>

                  {/* PAM */}
                  <a href="tel:4532348585" className="bg-white hover:bg-red-50 p-5 rounded-2xl flex items-center gap-4 transition-all hover:scale-[1.02] active:scale-95 shadow-lg group border-l-8 border-indigo-600">
                    <div className="bg-indigo-100 p-3 rounded-xl group-hover:-translate-y-1 transition-transform">
                      <Building2 className="w-8 h-8 text-indigo-600" />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-red-600 font-black text-xl md:text-2xl uppercase tracking-tighter leading-none">Pronto Atendimento</span>
                      <span className="text-indigo-600 font-bold text-lg">(45) 3234-8585</span>
                    </div>
                  </a>

                  {/* WhatsApp PM */}
                  <a 
                    href="https://wa.me/5545933001298" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="bg-white hover:bg-green-50 p-5 rounded-2xl flex items-center gap-4 transition-all hover:scale-[1.02] active:scale-95 shadow-lg group border-l-8 border-green-500"
                  >
                    <div className="bg-green-100 p-3 rounded-xl group-hover:rotate-12 transition-transform">
                      <svg className="w-8 h-8 text-[#25D366]" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-green-600 font-black text-xl md:text-2xl uppercase tracking-tighter leading-none">Viatura PM</span>
                      <span className="text-green-500 font-bold text-lg">(45) 93300-1298</span>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Clima */}
        {!searchTerm && (
          <section className="max-w-2xl mx-auto w-full">
            <h3 className="text-xl font-bold text-slate-800 mb-4 flex items-center gap-2 justify-center">
              <CloudSun className="w-5 h-5 text-blue-600" />
              Tempo em Catanduvas hoje
            </h3>
            <WeatherWidget />
          </section>
        )}

        <div className="space-y-12">
          {/* Resultados da Busca */}
          {searchTerm && (
            <>
              {filteredLinks.length === 0 && filteredTelefones.length === 0 && filteredCooperativas.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-slate-500 text-lg">Nenhum resultado encontrado para &quot;{searchTerm}&quot;</p>
                </div>
              )}

              {filteredLinks.length > 0 && (
                <section>
                  <h3 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-2">
                    <Search className="w-6 h-6 text-blue-600" />
                    Páginas e Serviços
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredLinks.map((l, i) => (
                      <Link 
                        key={i}
                        to={l.href}
                        className={`group relative flex flex-col items-center justify-center bg-gradient-to-br ${l.color} p-8 rounded-[2rem] shadow-xl hover:shadow-blue-200 transition-all hover:-translate-y-1 active:scale-95 overflow-hidden text-center`}
                      >
                        <div className="bg-white/20 p-4 rounded-2xl mb-4 backdrop-blur-sm group-hover:scale-110 transition-transform">
                          {l.icon}
                        </div>
                        <span className="text-white font-black text-xl uppercase tracking-tighter italic">{l.name}</span>
                        <span className="text-white/80 text-sm mt-2 font-bold">{l.description}</span>
                      </Link>
                    ))}
                  </div>
                </section>
              )}

              {filteredTelefones.length > 0 && (
                <section>
                  <h3 className="text-2xl font-bold text-slate-800 mb-2 flex items-center gap-2">
                    <Phone className="w-6 h-6 text-blue-600" />
                    Telefones Úteis
                  </h3>
                  <p className="text-slate-500 text-sm mb-4 font-medium italic">Clique no serviço desejado para ligar</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredTelefones.map((t, i) => (
                      <PhoneButton key={i} icon={t.icon} name={t.name} number={t.number} color={t.color} whatsapp={t.whatsapp} nameColor={(t as any).nameColor} numberColor={(t as any).numberColor} />
                    ))}
                  </div>
                </section>
              )}

              {filteredCooperativas.length > 0 && (
                <section>
                  <h3 className="text-2xl font-bold text-slate-800 mb-2 flex items-center gap-2">
                    <Building2 className="w-6 h-6 text-blue-600" />
                    Cooperativas de Crédito
                  </h3>
                  <p className="text-slate-500 text-sm mb-4 font-medium italic">Clique no serviço desejado para ligar</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {filteredCooperativas.map((c, i) => (
                      <PhoneButton 
                        key={i} 
                        icon={c.icon} 
                        name={c.name} 
                        number={c.number} 
                        color={c.color} 
                        whatsapp={c.whatsapp}
                        largeName
                      />
                    ))}
                  </div>
                </section>
              )}
            </>
          )}

          {/* Telefones Úteis */}
          {!searchTerm && (
            <section>
              <h3 className="text-2xl font-bold text-slate-800 mb-2 flex items-center gap-2">
                <Phone className="w-6 h-6 text-blue-600" />
                Telefones Úteis
              </h3>
              <p className="text-slate-500 text-sm mb-4 font-medium italic">Clique no serviço desejado para ligar</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {TELEFONES_UTEIS.map((t, i) => (
                  <PhoneButton key={i} icon={t.icon} name={t.name} number={t.number} color={t.color} whatsapp={t.whatsapp} nameColor={(t as any).nameColor} numberColor={(t as any).numberColor} />
                ))}
              </div>
            </section>
          )}

          {/* Cooperativas de Crédito */}
          {!searchTerm && (
            <section>
              <h3 className="text-2xl font-bold text-slate-800 mb-2 flex items-center gap-2">
                <Building2 className="w-6 h-6 text-blue-600" />
                Cooperativas de Crédito em Catanduvas
              </h3>
              <p className="text-slate-500 text-sm mb-4 font-medium italic">Clique no serviço desejado para ligar</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {COOPERATIVAS.map((c, i) => (
                  <PhoneButton 
                    key={i} 
                    icon={c.icon} 
                    name={c.name} 
                    number={c.number} 
                    color={c.color} 
                    whatsapp={c.whatsapp}
                    largeName
                  />
                ))}
              </div>
            </section>
          )}

          {/* Novos Botões de Sessões */}
          {!searchTerm && (
            <section>
              <h3 className="text-2xl font-bold text-slate-800 mb-6 flex items-center gap-2">
                <Users className="w-6 h-6 text-blue-600" />
                Guia de Contatos
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <Link 
                  to="/adm-municipal"
                  className="group relative flex flex-col items-center justify-center bg-gradient-to-br from-blue-600 to-indigo-700 p-8 rounded-[2rem] shadow-xl hover:shadow-blue-200 transition-all hover:-translate-y-1 active:scale-95 overflow-hidden text-center"
                >
                  <div className="bg-white/20 p-4 rounded-2xl mb-4 backdrop-blur-sm group-hover:scale-110 transition-transform">
                    <Building2 className="w-10 h-10 text-white" />
                  </div>
                  <span className="text-white font-black text-xl uppercase tracking-tighter italic">ADM Municipal</span>
                  <span className="text-blue-100 text-sm mt-2 font-bold">Telefones da Prefeitura</span>
                </Link>

                <Link 
                  to="/comercio"
                  className="group relative flex flex-col items-center justify-center bg-gradient-to-br from-emerald-600 to-teal-700 p-8 rounded-[2rem] shadow-xl hover:shadow-emerald-200 transition-all hover:-translate-y-1 active:scale-95 overflow-hidden text-center"
                >
                  <div className="bg-white/20 p-4 rounded-2xl mb-4 backdrop-blur-sm group-hover:scale-110 transition-transform">
                    <ShoppingCart className="w-10 h-10 text-white" />
                  </div>
                  <span className="text-white font-black text-xl uppercase tracking-tighter italic">Comércio</span>
                  <span className="text-emerald-100 text-sm mt-2 font-bold">Empresas por Categoria</span>
                </Link>

                <Link 
                  to="/taxis"
                  className="group relative flex flex-col items-center justify-center bg-gradient-to-br from-amber-500 to-orange-600 p-8 rounded-[2rem] shadow-xl hover:shadow-orange-200 transition-all hover:-translate-y-1 active:scale-95 overflow-hidden text-center"
                >
                  <div className="bg-white/20 p-4 rounded-2xl mb-4 backdrop-blur-sm group-hover:scale-110 transition-transform">
                    <Car className="w-10 h-10 text-white" />
                  </div>
                  <span className="text-white font-black text-xl uppercase tracking-tighter italic">Táxis</span>
                  <span className="text-orange-100 text-sm mt-2 font-bold">Número dos Taxistas</span>
                </Link>

                <Link 
                  to="/guinchos"
                  className="group relative flex flex-col items-center justify-center bg-gradient-to-br from-slate-600 to-slate-700 p-8 rounded-[2rem] shadow-xl hover:shadow-slate-200 transition-all hover:-translate-y-1 active:scale-95 overflow-hidden text-center"
                >
                  <div className="bg-white/20 p-4 rounded-2xl mb-4 backdrop-blur-sm group-hover:scale-110 transition-transform">
                    <Truck className="w-10 h-10 text-white" />
                  </div>
                  <span className="text-white font-black text-xl uppercase tracking-tighter italic">Guinchos</span>
                  <span className="text-slate-100 text-sm mt-2 font-bold">Serviço 24h</span>
                </Link>

                <Link 
                  to="/marmitas"
                  className="group relative flex flex-col items-center justify-center bg-gradient-to-br from-red-600 to-rose-700 p-8 rounded-[2rem] shadow-xl hover:shadow-red-200 transition-all hover:-translate-y-1 active:scale-95 overflow-hidden text-center"
                >
                  <div className="bg-white/20 p-4 rounded-2xl mb-4 backdrop-blur-sm group-hover:scale-110 transition-transform">
                    <Utensils className="w-10 h-10 text-white" />
                  </div>
                  <span className="text-white font-black text-xl uppercase tracking-tighter italic">Pedir Marmitas</span>
                  <span className="text-red-100 text-sm mt-2 font-bold">Pedir agora!!!</span>
                </Link>

                <Link 
                  to="/lanches-pizzas"
                  className="group relative flex flex-col items-center justify-center bg-gradient-to-br from-amber-600 to-yellow-700 p-8 rounded-[2rem] shadow-xl hover:shadow-amber-200 transition-all hover:-translate-y-1 active:scale-95 overflow-hidden text-center"
                >
                  <div className="bg-white/20 p-4 rounded-2xl mb-4 backdrop-blur-sm group-hover:scale-110 transition-transform">
                    <Pizza className="w-10 h-10 text-white" />
                  </div>
                  <span className="text-white font-black text-xl uppercase tracking-tighter italic">Pedir Lanches e Pizzas</span>
                  <span className="text-amber-100 text-sm mt-2 font-bold">Pedir agora!!!</span>
                </Link>
              </div>
            </section>
          )}
        </div>

        {/* Sobre o Sistema */}
        {!searchTerm && (
          <section className="max-w-5xl mx-auto">
            <div className="bg-white rounded-[2.5rem] p-8 md:p-12 shadow-2xl border border-slate-100 relative overflow-hidden">
              <div className="absolute top-0 right-0 -mt-12 -mr-12 w-48 h-48 bg-blue-50 rounded-full blur-3xl opacity-50"></div>
              <div className="absolute bottom-0 left-0 -mb-12 -ml-12 w-48 h-48 bg-indigo-50 rounded-full blur-3xl opacity-50"></div>
              
              <div className="relative z-10 flex flex-col md:flex-row gap-8 items-center">
                <div className="bg-blue-600 p-6 rounded-[2rem] shadow-xl shadow-blue-100 shrink-0">
                  <Info className="w-12 h-12 text-white" />
                </div>
                <div className="space-y-4">
                  <h3 className="text-3xl md:text-4xl font-black text-slate-800 uppercase tracking-tighter italic">
                    Como o CatanduvasPR.com.br ajuda você?
                  </h3>
                  <div className="space-y-4 text-slate-600 text-lg leading-relaxed font-medium">
                    <p>
                      O <strong className="text-blue-700">CatanduvasPR.com.br</strong> foi criado com um objetivo simples: facilitar o acesso à informação para todos os moradores e visitantes de nossa cidade.
                    </p>
                    <p>
                      Sabemos que em momentos de pressa ou necessidade, encontrar um telefone útil ou saber qual farmácia está de plantão pode ser um desafio. Por isso, reunimos em um único portal, de forma organizada e rápida, tudo o que você precisa:
                    </p>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                      <li className="flex items-center gap-2 bg-slate-50 p-3 rounded-xl border border-slate-100">
                        <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                        <span>Farmácias de Plantão 24h</span>
                      </li>
                      <li className="flex items-center gap-2 bg-slate-50 p-3 rounded-xl border border-slate-100">
                        <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                        <span>Telefones de Emergência</span>
                      </li>
                      <li className="flex items-center gap-2 bg-slate-50 p-3 rounded-xl border border-slate-100">
                        <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                        <span>Horários de Ônibus Atualizados</span>
                      </li>
                      <li className="flex items-center gap-2 bg-slate-50 p-3 rounded-xl border border-slate-100">
                        <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                        <span>Guia Comercial Completo</span>
                      </li>
                    </ul>
                    <p className="pt-4 font-bold text-slate-700">
                      Nosso sistema foi otimizado para celulares, permitindo que você ligue ou envie um WhatsApp com apenas um clique. É a tecnologia auxiliando nossa comunidade a estar mais conectada e informada.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        )}

      </main>

      <Footer />
    </div>
  );
}

function PhoneButton({ icon, name, number, color, whatsapp, largeName, nameColor = "text-white", numberColor = "" }: { icon: React.ReactNode, name: string, number: string, color: string, whatsapp?: string, largeName?: boolean, nameColor?: string, numberColor?: string }) {
  const isEmergency = number.length <= 3;
  const href = whatsapp ? whatsapp : (isEmergency ? `tel:${number}` : `tel:${number.replace(/\D/g, '')}`);
  
  return (
    <a 
      href={href}
      target={whatsapp ? "_blank" : undefined}
      rel={whatsapp ? "noopener noreferrer" : undefined}
      className={`flex items-center p-4 rounded-xl text-white transition-transform hover:scale-[1.02] active:scale-95 shadow-sm ${color}`}
    >
      <div className="bg-white/20 p-3 rounded-lg mr-4">
        {whatsapp ? (
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
        ) : icon}
      </div>
      <div>
        <p className={`${largeName ? 'text-2xl' : 'text-sm'} font-black ${nameColor} uppercase tracking-tighter leading-none mb-1`}>{name}</p>
        <p className={`${largeName ? 'text-sm' : 'text-xl'} font-bold tracking-wider opacity-90 ${numberColor}`}>{number}</p>
      </div>
    </a>
  );
}

function ServiceItem({ icon, name, action }: { icon: React.ReactNode, name: string, action: string }) {
  return (
    <li>
      <a href={action} className="flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors cursor-pointer group">
        <div className="bg-blue-100 text-blue-600 p-2.5 rounded-lg group-hover:bg-blue-600 group-hover:text-white transition-colors">
          {icon}
        </div>
        <span className="font-medium text-slate-700 group-hover:text-blue-700 transition-colors">{name}</span>
      </a>
    </li>
  );
}
